/**
 * `/docs/webhooks` — outbound webhook documentation.
 *
 * Structure:
 *   1. Overview — the four things users need to know about our
 *      webhook platform.
 *   2. Quickstart — point-and-click guide to a first successful
 *      delivery (uses the real Settings → Webhooks surface now).
 *   3. Payload shape — envelope + data shape for `audit.completed`,
 *      with links out to each event type.
 *   4. Signature verification — HMAC-SHA256 over `${t}.${body}`,
 *      sent as `X-Clickweave-Signature: t=<unix>,v1=<hex>`. This is
 *      the FROZEN format from README guardrail 2. Do not change it.
 *   5. Event catalogue — every event type we fire, when, with which
 *      `data` keys.
 *   6. Slack + Discord setup — URL formats, rendering quirks.
 *   7. Retries + dead-letter — 5 attempts, 1m → 5m → 25m → 2h → dead.
 *   8. Rotation — 24 h grace window with `signing_secret_previous`.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import {
  ArrowClockwise,
  ChatCircleText,
  DiscordLogo,
  Key,
  Paperclip,
  Plus,
  ShieldCheck,
  SlackLogo,
  WebhooksLogo,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/webhooks')({
  component: WebhooksDocs,
});

const EXAMPLE_PAYLOAD = JSON.stringify(
  {
    id: 'evt_01HWV0QY3XF5A9P8R7K2N6M3E1',
    event: 'audit.completed',
    occurred_at: '2026-04-01T12:34:53.221Z',
    delivered_at: '2026-04-01T12:34:54.512Z',
    attempt: 0,
    data: {
      id: 'aud_01hwv0qy3xf5a9p8r7k2n6m3e1',
      url: 'https://example.com',
      status: 'success',
      scores: { performance: 92, accessibility: 98, best_practices: 100, seo: 100 },
      metrics: {
        lcp_ms: 1840,
        cls: 0.03,
        inp_ms: 124,
        ttfb_ms: 210,
        fcp_ms: 812,
        si_ms: 2100,
        tbt_ms: 40,
      },
      report_url: 'https://api.clickweave.co/v1/audits/aud_01hwv0qy3xf5a9p8r7k2n6m3e1/report',
    },
  },
  null,
  2,
);

const VERIFY_NODE = `import crypto from 'node:crypto';

// X-Clickweave-Signature: t=1712005493000,v1=8d1c…
// \`t\` is a UNIX timestamp in MILLISECONDS.
// HMAC input is \`\${t}.\${body}\` (period-delimited).
// Reject anything older than 5 minutes to foil replay.
export function verify(rawBody: string, signatureHeader: string, secret: string): boolean {
  const parts = Object.fromEntries(
    signatureHeader.split(',').map((p) => p.split('=') as [string, string]),
  );
  const t = parts.t;
  const v1 = parts.v1;
  if (!t || !v1) return false;

  const ageMs = Math.abs(Date.now() - Number(t));
  if (ageMs > 5 * 60 * 1000) return false;

  const digest = crypto
    .createHmac('sha256', secret)
    .update(\`\${t}.\${rawBody}\`)
    .digest('hex');

  const a = Buffer.from(digest, 'hex');
  const b = Buffer.from(v1, 'hex');
  return a.length === b.length && crypto.timingSafeEqual(a, b);
}`;

const VERIFY_PYTHON = `import hmac, hashlib, time

# \`t\` is a UNIX timestamp in MILLISECONDS.
def verify(raw_body: bytes, signature_header: str, secret: str) -> bool:
    parts = dict(p.split('=', 1) for p in signature_header.split(','))
    t, v1 = parts.get('t'), parts.get('v1')
    if not t or not v1:
        return False

    if abs(time.time() * 1000 - int(t)) > 5 * 60 * 1000:
        return False

    digest = hmac.new(
        secret.encode(),
        f"{t}.".encode() + raw_body,
        hashlib.sha256,
    ).hexdigest()
    return hmac.compare_digest(digest, v1)`;

const EVENT_CATALOGUE: {
  id: string;
  label: string;
  when: string;
  dataKeys: string;
}[] = [
  {
    id: 'audit.completed',
    label: 'audit.completed',
    when: 'A snapshot finishes successfully.',
    dataKeys: 'id, url, status, scores, metrics, report_url',
  },
  {
    id: 'audit.failed',
    label: 'audit.failed',
    when: 'A snapshot errors out, times out, or is aborted.',
    dataKeys: 'id, url, status, error, report_url (when available)',
  },
  {
    id: 'audit.started',
    label: 'audit.started',
    when: 'A snapshot starts running. Opt-in — high volume.',
    dataKeys: 'id, url, status, started_at, region, device_profile_label',
  },
  {
    id: 'audit_group.completed',
    label: 'audit_group.completed',
    when: 'Every snapshot in a run reaches a terminal status. One event per run, with rollup counts.',
    dataKeys:
      'group_id, site_id, trigger, total, successes, errors, first_error, started_at, completed_at, attempt',
  },
  {
    id: 'site.updated',
    label: 'site.updated',
    when: 'A site is successfully updated. Carries the fields that actually changed.',
    dataKeys: 'site_id, changed fields',
  },
  {
    id: 'chat.conversation.created',
    label: 'chat.conversation.created',
    when: 'A chat conversation starts. Per-message events are deliberately not emitted.',
    dataKeys: 'conversation_id, created_via',
  },
  {
    id: 'usage.threshold.warning',
    label: 'usage.threshold.warning',
    when: 'Monthly usage on a plan axis (e.g. on-demand audits) crosses 80% of the included quota.',
    dataKeys:
      'axis, threshold, usage, included, remaining, usage_percent, plan, upgrade_url, manage_monitors_url?',
  },
  {
    id: 'usage.threshold.critical',
    label: 'usage.threshold.critical',
    when: 'Monthly usage on a plan axis crosses 90% of the included quota.',
    dataKeys:
      'axis, threshold, usage, included, remaining, usage_percent, plan, upgrade_url, manage_monitors_url?',
  },
];

function WebhooksDocs(): ReactElement {
  return (
    <Island breadcrumb={[{ label: 'Docs', href: '/docs' }, { label: 'Webhooks' }]}>
      <div className="mx-auto max-w-3xl space-y-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Webhooks</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Skip polling. Register an HTTPS endpoint (or a Slack / Discord incoming webhook) and
            we'll POST a signed event the moment an audit resolves or a monitor trips.
          </p>
          <div className="flex items-center gap-2 pt-2">
            <Button render={<Link to="/settings/webhooks/new" />}>
              <Plus aria-hidden="true" />
              Create endpoint
            </Button>
            <Button variant="outline" render={<Link to="/settings/webhooks" />}>
              Manage endpoints
            </Button>
          </div>
        </header>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <WebhooksLogo
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              What you need to know
            </h2>
          </div>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-[var(--color-mute)]">
            <li>
              <strong className="text-[var(--color-ink)]">Signing is opt-in per endpoint.</strong>{' '}
              New endpoints are created unsigned. Flip{' '}
              <span className="font-mono text-xs">signing_enabled</span> on (UI toggle or PATCH) to
              start sending the <span className="font-mono text-xs">X-Clickweave-Signature</span>{' '}
              header. The signing secret is generated and stored on every endpoint regardless, so
              enabling later doesn't require a rotation. Slack and Discord don't verify signatures —
              leave it off for those.
            </li>
            <li>
              <strong className="text-[var(--color-ink)]">Subscriptions are additive.</strong> Leave
              the event-types list empty to receive everything, or tick specific events to scope the
              endpoint.
            </li>
            <li>
              <strong className="text-[var(--color-ink)]">At-least-once delivery.</strong> Every
              event carries a stable <span className="font-mono text-xs">id</span> in the envelope
              body, unchanged across retries and replays. Dedupe on that if your handler is
              at-least-once sensitive.
            </li>
            <li>
              <strong className="text-[var(--color-ink)]">Slack + Discord are first-class.</strong>{' '}
              Pick the <span className="font-mono text-xs">slack</span> or{' '}
              <span className="font-mono text-xs">discord</span> kind when registering; we'll
              transform the payload into their respective formats automatically.
            </li>
          </ol>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Paperclip
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Payload shape</h2>
          </div>
          <p className="text-sm text-[var(--color-mute)]">
            Every generic-kind delivery is{' '}
            <span className="font-mono text-xs">application/json</span> with the envelope below. The{' '}
            <span className="font-mono text-xs">event</span> discriminator tells you which{' '}
            <span className="font-mono text-xs">data</span> shape to expect — see the event
            catalogue.
          </p>
          <CodeSnippet title="POST /your/endpoint" code={EXAMPLE_PAYLOAD} />
          <p className="text-xs text-[var(--color-mute)]">
            Slack and Discord endpoints receive the channel-native formats (Block Kit / embeds)
            instead of the raw envelope. Signing is off for those by default — Slack and Discord
            don't verify webhook signatures (the URL itself is the auth). Enable signing only if you
            proxy the delivery through your own server first.
          </p>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <ShieldCheck
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              Signature verification
            </h2>
          </div>
          <p className="text-sm text-[var(--color-mute)]">
            When <span className="font-mono text-xs">signing_enabled</span> is on, every request
            carries an <span className="font-mono text-xs">X-Clickweave-Signature</span> header
            shaped like <span className="font-mono text-xs">t=1712005493000,v1=8d1c…</span>, where{' '}
            <span className="font-mono text-xs">t</span> is a UNIX timestamp in milliseconds.
            Recompute{' '}
            <span className="font-mono text-xs">
              HMAC-SHA256(secret, `${'{t}'}.${'{body}'}`)
            </span>{' '}
            and compare against <span className="font-mono text-xs">v1</span> in constant time.
            Reject deliveries where <span className="font-mono text-xs">t</span> is more than 5
            minutes old (replay window).
          </p>
          <CodeSnippet
            languages={[
              { id: 'node', label: 'Node', body: VERIFY_NODE },
              { id: 'python', label: 'Python', body: VERIFY_PYTHON },
            ]}
          />
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <ChatCircleText
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Event catalogue</h2>
          </div>
          <div className="overflow-hidden rounded-2xl border border-[var(--color-line)]">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-[var(--color-line-soft)] bg-[var(--color-paper-alt)] text-left uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">When it fires</th>
                  <th className="px-3 py-2 font-medium">
                    <span className="font-mono">data</span> keys
                  </th>
                </tr>
              </thead>
              <tbody>
                {EVENT_CATALOGUE.map((e) => (
                  <tr key={e.id} className="border-b border-[var(--color-line-soft)]/70 align-top">
                    <td className="px-3 py-2 font-mono text-[var(--color-ink)]">{e.label}</td>
                    <td className="px-3 py-2 text-[var(--color-mute)]">{e.when}</td>
                    <td className="px-3 py-2 font-mono text-[var(--color-ink-soft)]">
                      {e.dataKeys}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <SlackLogo
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Slack setup</h2>
          </div>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-[var(--color-mute)]">
            <li>
              In Slack, open{' '}
              <a
                className="underline"
                href="https://api.slack.com/messaging/webhooks"
                target="_blank"
                rel="noreferrer"
              >
                Incoming Webhooks
              </a>{' '}
              for your workspace and add a new hook pointing at the channel you want. Copy the URL —
              it looks like{' '}
              <span className="font-mono text-xs">https://hooks.slack.com/services/T…/B…/…</span>.
            </li>
            <li>
              In Settings → Webhooks, click <strong>Create endpoint</strong>, pick{' '}
              <strong>Slack</strong>, and paste the URL.
            </li>
            <li>
              Open the endpoint and hit <strong>Send test event</strong>. A Block Kit card with a
              synthetic score + metrics lands in the configured channel.
            </li>
          </ol>
          <p className="text-xs text-[var(--color-mute)]">
            We encode channel + auth into the URL — no OAuth required. Slack URLs that don't match{' '}
            <span className="font-mono">hooks.slack.com/services/…</span> are rejected at create
            time.
          </p>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <DiscordLogo
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">Discord setup</h2>
          </div>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-[var(--color-mute)]">
            <li>
              In Discord, open the channel settings → <strong>Integrations</strong> →{' '}
              <strong>Webhooks</strong> → <strong>New Webhook</strong>. Copy the URL; it looks like{' '}
              <span className="font-mono text-xs">https://discord.com/api/webhooks/…/…</span>.
            </li>
            <li>
              In Settings → Webhooks, pick <strong>Discord</strong> and paste the URL.
            </li>
            <li>
              Send a test event. A coloured embed (green for success, amber for warning, red for
              failure) with scores + report link renders in the channel.
            </li>
          </ol>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <ArrowClockwise
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              Retries &amp; dead-letter
            </h2>
          </div>
          <ul className="list-disc space-y-1 pl-5 text-sm text-[var(--color-mute)]">
            <li>2xx → delivered. Redirects are followed; a 3xx that still surfaces is retried.</li>
            <li>5xx, 408, 429, network error → retry. 4xx (except 408/429) → fail fast.</li>
            <li>
              Retry schedule: <span className="font-mono">1m → 5m → 25m → 2h</span> (5 attempts
              total). After the last attempt the message is marked{' '}
              <span className="font-mono">dead</span>.
            </li>
            <li>
              After a configurable number of consecutive failures the endpoint is{' '}
              <strong>auto-disabled</strong>. Re-enable manually from the settings page — that
              clears the failure counter and resumes delivery.
            </li>
            <li>
              Every failed / dead message can be replayed individually from{' '}
              <strong>Deliveries</strong>. Replay creates a fresh message with{' '}
              <span className="font-mono">reason: 'manual_replay'</span> — the original row is
              preserved.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Key
              aria-hidden="true"
              size={20}
              weight="light"
              className="text-[var(--color-mute)]/80"
            />
            <h2 className="text-base font-semibold text-[var(--color-ink)]">
              Rotating signing secrets
            </h2>
          </div>
          <ol className="list-decimal space-y-1.5 pl-5 text-sm text-[var(--color-mute)]">
            <li>
              In Settings → Webhooks, click the rotate icon for the endpoint you want to refresh. We
              generate a new secret server-side, demote the current one to a 24h fallback, and hand
              the new secret back in the confirmation dialog. The new secret is also visible on the
              endpoint detail page afterwards.
            </li>
            <li>
              Ship the new secret to your receiver. Until the grace window expires <em>both</em>
              the new and previous secrets verify; pick whichever one matches. The detail page shows
              both during the grace window.
            </li>
            <li>
              After 24 h the previous secret is purged. Receivers still pinned to it start failing
              verification — watch the deliveries log for spikes in{' '}
              <span className="font-mono">failed</span> status.
            </li>
          </ol>
        </section>
      </div>
    </Island>
  );
}
